// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Component } from './component';
import { addStatusMessage, showInternalError, clearStatusMessages, getComponent } from './common';
import { redirect, redirectPost } from './form-redirect';

/**
 * @class Jsw.FormAjax
 * @extends Jsw.Component
 */
export const FormAjax = Class.create(Component, {
    _actionButtonTitle: '',

    _initConfiguration($super, config) {
        $super(config);
        this._sendButtonId = this._getConfigParam('sendButtonId', 'btn-send');
        this._applyButtonId = this._getConfigParam('applyButtonId', 'btn-apply');
        this._cancelButtonId = this._getConfigParam('cancelButtonId', 'btn-cancel');
        this._submitHandler = this._getConfigParam('submitHandler', () => true);
    },

    disable() {
        [this._sendButtonId, this._applyButtonId, this._cancelButtonId].each(function (buttonId) {
            this._toggleButton(buttonId, true);
        }, this);

        const actionButton = this._getActionButton();
        if (actionButton) {
            this._actionButtonTitle = actionButton.innerHTML;
            actionButton.update(`<span class="wait">${this._config.waitButtonTitle}</span>`);
        }
    },

    enable() {
        [this._sendButtonId, this._applyButtonId, this._cancelButtonId].each(function (buttonId) {
            this._toggleButton(buttonId, false);
        }, this);

        const actionButton = this._getActionButton();
        if (actionButton) {
            actionButton.update(this._actionButtonTitle);
        }
    },

    _toggleButton(buttonId, disable) {
        const buttonWrapper = $(buttonId);
        if (buttonWrapper) {
            buttonWrapper.down('button').disabled = disable;
            disable ? buttonWrapper.addClassName('disabled') : buttonWrapper.removeClassName('disabled');
        }
    },

    _getActionButton() {
        const actionButtonWrapper = $(this._componentElement.noRedirect ? this._applyButtonId : this._sendButtonId);
        if (actionButtonWrapper) {
            return actionButtonWrapper.down('button');
        }

        return null;
    },

    _initComponentElement() {
        this._componentElement = $(this._id);
        this._initDisablerOverlay();

        const firstElement = this._componentElement.findFirstElement();

        if ('undefined' !== typeof firstElement) {
            try {
                firstElement.focus();
            } catch (e) {}
        }

        this._submitInProgress = false;
    },

    _addEvents() {
        this._componentElement._formSubmit = this._componentElement.submit;
        this._componentElement.submit = this._onSubmit.bind(this);
        this._componentElement.observe('submit', this._onSubmitEvent.bind(this));
        this._addChoiceRadioButtonsOnClickEvent();
    },

    _addChoiceRadioButtonsOnClickEvent() {
        $$('input[type="radio"]').each(function (element) {
            if (!element.up('div.choice-block')) {
                return;
            }
            element.observe('click', function () {
                element.up('div.choice-block').select('span').each(function (element) {
                    element.removeClassName('selected');

                    if ('0' === element.value) {
                        element.removeClassName('no');
                    }
                });

                element.up('span').addClassName('selected');

                if ('0' === element.value) {
                    element.up('span').addClassName('no');
                }
            });
        });
    },

    _onSubmitEvent(event) {
        this._onSubmit();
        Event.stop(event);
        return false;
    },

    _onSubmit() {
        if (false === this._submitHandler()) {
            return false;
        }

        if (this._submitInProgress) {
            return false;
        }
        this._submitInProgress = true;

        this.disable();

        if (this._componentElement.enctype === 'multipart/form-data') {
            this._componentElement._formSubmit();
            return true;
        }

        let actionUrl = this._componentElement.getAttribute('action');

        if (!actionUrl) {
            actionUrl = $(document).location.href;
        }

        // remove hash symbol (and the following) cause this will encoded and lead to problems
        actionUrl = actionUrl.replace(/#.*$/, '');

        const params = this._componentElement.serialize();
        new Ajax.Request(actionUrl, {
            method: 'post',
            parameters: params,
            onSuccess: this._onSuccess.bind(this),
            onFailure: this._onFailure.bind(this),
        });
        return true;
    },

    _onFailure(req) {
        this._submitInProgress = false;
        this._clearMessages();

        if (504 === req.status) {
            addStatusMessage('error', this._config.timeoutMessage);
        } else {
            showInternalError(req.responseText);
        }

        this.enable();
    },

    _onSuccess(req) {
        this._submitInProgress = false;
        clearStatusMessages();

        try {
            const response = req.responseText.evalJSON();
            this._processForm(response);
        } catch (e) {
            this._clearMessages();
            this.enable();

            // show error, if request wasn't aborted by user
            if (0 != req.status) {
                showInternalError(req.responseText);
            }
        }
    },

    _processForm(response) {
        if (response.componentType === 'Jsw.Task.ProgressBar.Item') {
            getComponent('asyncProgressBarWrapper').progressDialog(response);
            return;
        }

        if (response.redirect) {
            this._processResponseRedirect(response);
            return;
        }

        this._clearMessages();
        this._processResponseStatus(response.status);
        this._processResponseStatusMessages(response.statusMessages);
        this._processResponseFormMessages(response.formMessages);

        if (this._hasFieldErrors) {
            this._showFieldErrorArea();
        }
        this.enable();
    },

    _showFieldErrorArea() {
        const errorsElements = this._componentElement.select('.field-errors');
        let firstError = null;

        errorsElements.each(function (errorElement) {
            if (errorElement.visible()) {
                firstError = errorElement;
            }
        });

        let hiddenContainerId = null;

        firstError.ancestors().each(function (element) {
            if (!element.visible()) {
                hiddenContainerId = element.id;
            }
        });

        if (!hiddenContainerId) {
            return;
        }

        // in case of tabbed form we must switch to corresponding tab
        const tabsBar = getComponent('form-tab-buttons');

        if (tabsBar) {
            tabsBar.switchTab(hiddenContainerId);
        }
    },

    _processResponseStatus(status) {
        if ('success' !== status) {
            this._hasErrors = true;
        }
    },

    _clearMessages() {
        // clear field messages
        try {
            this._componentElement.select('.field-errors').each(function (errors) {
                errors.hide();
                const rowElement = errors.up('.form-row');
                if (rowElement) {
                    rowElement.removeClassName('error');
                }
                errors.select('.error-hint').each(function (error) {
                    error.remove();
                });
            });
        } catch (e) {
        }

        this._hasErrors = false;
        this._hasFieldErrors = false;
    },

    _processResponseStatusMessages(messages) {
        $A(messages).each(function (value) {
            this._addFormMessage(value.status, value.content, value.title);
        }, this);
    },

    _processResponseFormMessages(messages) {
        this._processFieldMessages(messages, []);
    },

    _addFieldMessage(errors, message) {
        errors.up('.form-row').addClassName('error');
        errors.insert({ bottom: `<span class="error-hint">${message.escapeHTML()}</span>` });
        errors.show();
    },

    _processFieldMessage(prefix, name, message) {
        let fieldErrors;
        const formElement = this._componentElement.down(`#${prefix.join('-')}`);
        fieldErrors = formElement ? formElement.next('.field-errors') : null;
        if (!fieldErrors) {
            fieldErrors = formElement ? formElement.up('.form-row').down('.field-errors') : null;
        }
        if (!fieldErrors) {
            fieldErrors = this._componentElement.down(`#${prefix.join('-')}-form-row`).select('.field-errors').last();
        }
        this._addFieldMessage(fieldErrors, message);
        this._hasFieldErrors = true;
    },

    _processFieldMessages(messages, prefix) {
        if (Object.isArray(messages)) {
            $A(messages).each(function (message) {
                if (Object.isString(message)) {
                    this._processFieldMessage(prefix, 'error', message);
                } else {
                    prefix.push(name);
                    this._processFieldMessages(message, prefix);
                    prefix.pop();
                }
            }, this);
        } else {
            $H(messages).each(function (pair) {
                if (Object.isString(pair.value)) {
                    this._processFieldMessage(prefix, pair.key, pair.value);
                } else {
                    prefix.push(pair.key);
                    this._processFieldMessages(pair.value, prefix);
                    prefix.pop();
                }
            }, this);
        }
    },

    _addFormMessage(type, message, title) {
        addStatusMessage(type, message, { title });
    },

    _processResponseRedirect(response) {
        if (this._componentElement.noRedirect) {
            document.location.reload();
        } else if (response.postData) {
            redirectPost(response.redirect, response.postData, response.target);
        } else {
            redirect(response.redirect, null, response.target);
        }
    },
});



// WEBPACK FOOTER //
// ./jsw/form-ajax.js